import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
    FEATURE_GENERIC_TASKS,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    hasAnyPermission,
    hasFeatureFlag,
    PersonRolesDto,
    PersonService,
    PersonsOrganisationDto,
    PreferencesService,
    toTimestamp,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe, FormatPersonPipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import {
    applyPreferences,
    findDefaultColumnSelection,
    updateDateRange
} from 'libs/flyfreely-table/src/lib/helpers';
import {
    GeneralTask,
    TaskStatus
} from 'libs/flyfreely/src/lib/services/generalTasks.service';
import { GeneralTasksDialoguesService } from 'libs/outstanding-tasks/src/lib/widgets/general-task/general-tasks-dialogues.service';
import { combineLatest, EMPTY, forkJoin, Subject } from 'rxjs';
import { catchError, startWith, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../outstanding-tasks-view.service';
import {
    formatGeneralTasksPriority,
    formatGeneralTasksStatuses,
    GeneralTasksDataService
} from './general-tasks-data.service';

const GENERAL_IDENTIFIER = 'generalTasksWidget';

const priorityOptions = Object.entries(formatGeneralTasksPriority).map(
    ([value, name]) => ({ name, value })
);
const statusOptions = Object.entries(formatGeneralTasksStatuses).map(
    ([value, name]) => ({ name, value })
);

@Component({
    selector: 'general-tasks-widget',
    templateUrl: './general-tasks-widget.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [GeneralTasksDialoguesService],
    styleUrls: ['./general-tasks-widget.component.scss']
})
export class GeneralTasksWidgetComponent implements OnInit, OnDestroy {
    organisation: PersonsOrganisationDto;
    canAddTask: boolean;
    showBody = true;

    generalTasks: GeneralTask[];

    availableColumns: TableColumn[];
    defaultAvailableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    columnSorting: ColumnSortPreferences;
    tableSearch: any;

    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    protected readonly formatPriority = formatGeneralTasksPriority;
    protected readonly formatStatuses = formatGeneralTasksStatuses;

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private generalTasksDataService: GeneralTasksDataService,
        private formatDatePipe: FormatDatePipe,
        private generalTasksDialoguesService: GeneralTasksDialoguesService,
        private preferencesService: PreferencesService,
        private personService: PersonService,
        private formatPerson: FormatPersonPipe,
        private changeDetector: ChangeDetectorRef
    ) {
        combineLatest([
            this.workTracker.observable.pipe(startWith(false)),
            this.generalTasksDataService.working$.pipe(startWith(false))
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([working, serviceWorking]) =>
                    (this.working = working || serviceWorking)
            );

        this.tableConfig = {
            serverPagination: true,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.availableColumns = [
            {
                value: 'summary',
                name: 'Title',
                searchable: true,
                selectable: false,
                sortable: false,
                defaultSelection: true
            },
            {
                value: 'assignee',
                name: 'Assignee',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                searchOptions: []
            },
            {
                value: 'reporter',
                name: 'Reporter',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                searchOptions: []
            },
            {
                value: 'dueDate',
                name: 'Due Date',
                searchable: 'daterange',
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => this.formatDatePipe.transform(t)
            },
            {
                value: 'priority',
                name: 'Priority',
                searchable: 'selection',
                selectable: false,
                defaultSelection: true,
                searchOptions: priorityOptions
            },
            {
                value: 'status',
                name: 'Status',
                searchable: 'selection',
                selectable: false,
                defaultSelection: true,
                searchOptions: statusOptions
            }
        ];
        this.defaultAvailableColumns = this.availableColumns;
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.generalTasks = [];
                this.loadPreferences();
                this.refreshPermissions();
            });

        combineLatest([
            this.generalTasksDataService.generalTasks$,
            this.generalTasksDataService.currentPage$,
            this.generalTasksDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([tasks, currentPage, totalItems]) => {
                this.generalTasks = tasks;
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.generalTasksDataService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshTasks();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onTableSearch(search: any) {
        this.tableSearch = search;
        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: search
        };
        if (this.tableConfig != null) {
            this.tableConfig.currentPage = 0;
        }
        this.updateUserPreferences();
        this.refreshTasks();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    // Reset the user preferences on organisation change
    private resetUserPreferences() {
        this.userPreferences = null;
        this.availableColumns = this.defaultAvailableColumns;
        this.selectedColumns = findDefaultColumnSelection(
            this.availableColumns
        );
        this.columnSorting = null;
        const updatedConfig = {
            ...this.tableConfig,
            currentPage: 0,
            limit: 3
        };
        this.tableConfig = updatedConfig;
        this.showBody = true;
        this.tableSearch = null;
    }

    private loadPreferences() {
        this.resetUserPreferences();
        if (!this.organisation) {
            return;
        }
        forkJoin([
            this.preferencesService
                .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                    'generalTasksWidgetTable',
                    this.organisation.id
                )
                .pipe(
                    catchError(() => {
                        this.selectedColumns = findDefaultColumnSelection(
                            this.availableColumns
                        );
                        return EMPTY;
                    })
                ),
            this.personService.findPersonnel(this.organisation.id)
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([preferences, personList]) => {
                this.setupTableColumns(personList);
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
                this.changeDetector.detectChanges();
                this.refreshTasks();
            })
            .add(this.workTracker.createTracker);
    }

    private setupTableColumns(personList: PersonRolesDto[]) {
        const personFilters = personList.map(person => ({
            name: this.formatPerson.transform(person),
            value: person.id
        }));

        const updatedColumns = this.availableColumns.map(column => {
            if (column.value === 'assignee' || column.value === 'reporter') {
                column.searchOptions = personFilters;
            }
            return column;
        });
        this.availableColumns = updatedColumns;
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private refreshPermissions() {
        this.canAddTask = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.TASK_ADD
        );
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        const updatedConfig = {
            ...this.tableConfig,
            limit: this.userPreferences.itemLimit
        };
        this.tableConfig = updatedConfig;
        this.showBody = this.userPreferences.showBody ?? true;
        this.tableSearch = updateDateRange(
            this.userPreferences?.tableSearch ?? {},
            'dueDate'
        );
    }

    viewElement(task: GeneralTask) {
        if (task.status === TaskStatus.NEW) {
            this.generalTasksDialoguesService.showTaskEditDialogue(
                task,
                this.organisation
            );
        } else {
            this.generalTasksDialoguesService.showTaskDetailsDialogue(
                task,
                this.organisation
            );
        }
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshTasks();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    refreshTasks() {
        const parsedTableSearch = this.tableSearch
            ? {
                  reporterId: this.tableSearch.reporter,
                  assigneeId: this.tableSearch.assignee,
                  text: this.tableSearch.summary,
                  status: this.tableSearch.status,
                  priority: this.tableSearch.priority,
                  ...(this.tableSearch.dueDate != null
                      ? {
                            dueDate: {
                                start: toTimestamp(this.tableSearch.dueDate[0]),
                                end: toTimestamp(this.tableSearch.dueDate[1])
                            }
                        }
                      : {})
              }
            : {};
        this.generalTasksDataService.updateTaskTable({
            organisationId: this.organisation.id,
            page: this.tableConfig.currentPage ?? 0,
            limit: this.tableConfig.limit ?? 3,
            sorting: this.columnSorting,
            ...parsedTableSearch
        });
    }

    onAddNewTask() {
        this.generalTasksDialoguesService.showAddTaskDialogue(
            this.organisation
        );
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'generalTasksWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }
}

export const generalTasksWidgetRegistration: WidgetRegistrationWithSection = {
    widgetIdentifier: GENERAL_IDENTIFIER,
    section: 'general',
    component: GeneralTasksWidgetComponent,
    isAvailable: organisation =>
        hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW) &&
        hasFeatureFlag(organisation, FEATURE_GENERIC_TASKS)
};
